<script setup lang="ts">
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha'
import { onMounted, useTemplateRef } from 'vue'

import { getEnv } from '@/helpers/environment'

import { errorTracker } from '@/services/errorTracking'

import { useIdentityStore } from '@/stores/identity'

import ValidationError from '@/errors/ValidationError'

// Out of scope: wallet-sheet token refresh (no wallet support yet).

export type CaptchaTokenResult = { status: 'success'; token: string | undefined } | { status: 'error'; error: Error }

interface VueHcaptchaInstance {
  executeAsync(): Promise<{ response: string; key: string }>
  reset(): void
}

const env = getEnv()
const hCaptchaKey = env.config.captcha?.hcaptcha_public as string | undefined
const identityStore = useIdentityStore()
// Not enabled if hCaptchaKey is undefined or empty string.
const enabled = Boolean(hCaptchaKey)

const widget = useTemplateRef<InstanceType<typeof VueHcaptcha>>('widget')

onMounted(() => {
  const instance = widget.value
  if (!instance) return

  if (typeof instance.executeAsync !== 'function' || typeof instance.reset !== 'function') {
    errorTracker.track(new Error('VueHcaptcha instance is missing executeAsync/reset.'))
    return
  }
})

/** Server-side tokens are one-shot — call once per API request. */
async function getToken(): Promise<CaptchaTokenResult> {
  if (!enabled) return { status: 'success', token: undefined }

  // Member checkout (`checkoutMy`) is gated on session identity, not an embedded user row. Captcha is only validated on guest checkout.
  if (identityStore.isLoggedIn) return { status: 'success', token: undefined }

  const instance = widget.value as VueHcaptchaInstance | null
  if (!instance) {
    // Should be unreachable: `enabled` true implies the v-if rendered
    // <VueHcaptcha> and the template ref is populated by mount.
    return { status: 'error', error: new Error('<CaptchaWidget>: getToken() called before mount') }
  }

  try {
    const { response } = await instance.executeAsync()
    return { status: 'success', token: response }
  } catch (error) {
    // challenge-closed — user closed the challenge.
    // challenge-expired — user took too long to solve the challenge.
    if (error === 'challenge-closed' || error === 'challenge-expired') {
      return { status: 'error', error: new ValidationError() }
    }

    // hCaptcha rejects with raw strings for user actions and Error-likes for
    // transport / config issues. Normalize to a real Error.
    return { status: 'error', error: new Error('hCaptcha challenge failed', { cause: error }) }
  }
}

function reset() {
  ;(widget.value as VueHcaptchaInstance | null)?.reset()
}

defineExpose({ getToken })
</script>

<template>
  <template v-if="enabled">
    <p class="captcha-notice">
      This site is protected by hCaptcha and its
      <a href="https://www.hcaptcha.com/privacy">Privacy Policy</a> and
      <a href="https://www.hcaptcha.com/terms">Terms of Service</a> apply.
    </p>
    <VueHcaptcha ref="widget" :sitekey="hCaptchaKey" size="invisible" @expired="reset" />
  </template>
</template>

<style scoped>
.captcha-notice {
  font-size: 0.85em;
  color: var(--text-secondary);
}

.captcha-notice a {
  color: var(--text-link);
  text-decoration: underline;
}
</style>
